import { Collection, PaymentModule } from "../../survey";

export const generateDebitCardCollection = (config: PaymentModule): Collection => ({
  "kind": "Collection",
  "name": {
    "en": "Debit Cards"
  },
  "options": {},
  "components": [
    ...(config.debit.kind === 'Usio') ? [{
      "kind": "Collection",
      "name": {
        "en": "USIO"
      },
      "options": {},
      "components": [
        {
          "kind": "Section",
          "title": {
            "en": "Application USIO"
          },
          "components": [
            {
              "kind": "Explanation",
              "content": {
                "en": "# NOTE: A New Akimbo Key is required for each program. At the moment, this has to be requested from USIO."
              }
            },
            {
              "kind": "Explanation",
              "content": {
                "en": "# Payment Information and Preferences \n\n**We are requesting payment information and preferences to compensate you for completion of this survey. If you are selected to participate, we will also include this payment information as your default option to receive guaranteed income payments.** \n\n**There are four ways you can receive funds.** \n\n**1. Have funds sent to your bank account.**\n\n**2. Have funds sent to a pre-paid debit card that you pick up from $program_name.** \n\n**3. Have funds sent to a virtual pre-paid debit card that will be sent to you by email and text.** \n\n**4. Have funds sent to to a pre-paid debt card that will be mailed to your home address.**\n\n*Please carefully read the information about each payment method before making your decision.*\n",
              }
            },
            {
              "kind": "Explanation",
              "content": {
                "en": "## Pre-paid Debit Card Pick Up\n\nThe second option is a pre-paid card that you will make an appointment with $program_name to pick up at their office. \n\nWhen you arrive at the office your identity will be verified and you will be issued a card. You can use this pre-paid card like any other debit card. However, pre-paid cards have less protections than a bank account. \n\nIf your card is lost of stolen report this to $program_name right away! Please note that for security purposes, we are not able to give cards to someone that you designate to collect your card.",
              }
            },
            {
              "kind": "Explanation",
              "content": {
                "en": "## Virtual Pre-paid Debit Card Information \n\nThe third option is a virtual pre-paid debit card. A virtual (or online) debit card is a debit card that you can use without providing a physical card. The Akimbo Now Card is the type of card AidKit provides.\n\nOnce the card is activated, you can use it for online purchases and with a digital/mobile wallet. Digital/mobile wallets include Apple Pay, Google Pay, and Samsung Pay. \n\nYou can also request: \n\n(1) An ACH transfer to a bank account\n\n(2) A physical card be sent to you at your residence (if you are able to receive mail) or at a Post Office Box\n\nYou will need to plan for the time each of these options will take to process. \n\n**Please be aware, it can be difficult to withdraw funds from a virtual debit card. While many ATMs advertise tap-to-pay or mobile wallet technology, most limit those transactions to people with debit accounts at that bank.**\n\nIf you need to withdraw cash immediately you can complete an online transaction at a retailer that offers cash pick-up services, such as Western Union or MoneyGram. Those services charge fees. You should comparison shop fees and ensure you can meet the vendor's standard for getting cash. This may include having an ID, providing your social security number or ITIN, or other documentation. We cannot guarantee that any transaction with a third party will be accepted.",
              }
            },
            {
              "kind": "Explanation",
              "content": {
                "en": "## Pre-paid Debit Card Mailed to You \n\nThe fourth option is a pre-paid debit card that is mailed to you. **Mailed cards take 3-4 weeks to arrive.** \n\nA card will be mailed to your address on file. Please be sure the address you provided in your application is a safe address before choosing this option. A P.O. Box cannot be used.",
              }
            },
            {
              "kind": "Select",
              "targetField": "applicant_payment_type",
              "content": {
                "en": "**Which payment method are you choosing?**\n\n*If you have a bank account or are able to sign up for one, direct deposit is the most secure and convenient option.*",
              },
              "choices": [
                {
                  "value": "usio_pickup",
                  "label": {
                    "en": "Debit Card Pick-up",
                  }
                },
                {
                  "value": "usio_mail",
                  "label": {
                    "en": "Mailed Debit Card"
                  }
                },
                {
                  "value": "usio_virtual",
                  "label": {
                    "en": "Virtual Debit Card"
                  }
                }
              ]
            },
            {
              "kind": "Conditional Block",
              "components": [
                {
                  "kind": "Address",
                  "targetField": "usio_mailing_address",
                  "content": {
                    "en": "**Please enter the mailing address where we should send the debit card**",
                  }
                },
                {
                  "kind": "Validated",
                  "targetField": "validate_usio_address",
                  "content": {
                    "en": "The address must be in California",
                  },
                  "formula": "if (info.usio_mailing_address_state !== \"CA\") {\n    throw '$content';\n}\nreturn 'hide_ok';"
                },
                {
                  "kind": "Validated",
                  "targetField": "validate_usio_address_po_box",
                  "content": {
                    "en": "The address must be a physical address.  It cannot be a P.O. Box",
                  },
                  "formula": "if (!info.usio_mailing_address_street) return 'hide_ok';\n\nconst poBoxVariations = [\n    'po box', 'p.o. box', 'p o box', 'p.o.box', 'po.box', 'pobox', \n    'p o b', 'p.o.b', 'pob'\n];\n\nconst containsPOBox = (address) => {\n    const addressLower = address.toLowerCase();\n    return poBoxVariations.some(variation => addressLower.includes(variation));\n};\n\nif (containsPOBox(info.usio_mailing_address_street)) {\n    throw '$content';  \n}\n\nreturn 'hide_ok';"
                }
              ],
              "conditionalOn": {
                "field": "applicant_payment_type",
                "kind": "Equals",
                "value": "usio_mail"
              }
            }
          ]
        },
        {
          "kind": "Section",
          "title": {
            "en": "USIO Debit Card Pick Up",
          },
          "components": [
            {
              "kind": "Explanation",
              "content": {
                "en": "# Debit Card Association",
              }
            },
            {
              "kind": "Show Field",
              "targetField": "legal_name",
              "headerText": {
                "en": "Legal Name",
              }
            },
            {
              "kind": "Show Field",
              "targetField": "selfie",
              "headerText": {
                "en": "Selfie",
              }
            },
            {
              "kind": "Show Field",
              "targetField": "id_doc",
              "headerText": {
                "en": "Photo ID",
              }
            },
            {
              "kind": "Confirmation",
              "targetField": "dc_confirm_identity",
              "content": {
                "en": "The debit card association process is available only after you confirm that the applicant's identity and documentation matches what is in the system.\n\n**Check here only if the applicant's identity and supporting documentation matches what is in the system.**",
              }
            },
            {
              "kind": "Conditional Block",
              "components": [
                {
                  "kind": "Explanation",
                  "content": {
                    "en": "In order to receive payments to your card, please input the 10 character ID on the back of your card beneath \"AidKit\", and then the last 4 digits of the card number.",
                  }
                },
                {
                  "kind": "Card Management",
                  "targetField": "card_id",
                  "cardType": "USIO"
                },
                {
                  "kind": "Confirmation",
                  "targetField": "dc_confirm_pickup",
                  "content": {
                    "en": "Check here once Debit Card has been associated and given to the applicant",
                  }
                },
                {
                  "kind": "Explanation",
                  "content": {
                    "en": `All set! \n\nHave questions about the payment? Visit the Payments FAQ page: https://${config.program}.aidkit.org/p/payments-faq`,
                  }
                }
              ],
              "conditionalOn": {
                "field": "dc_confirm_identity",
                "kind": "Equals",
                "value": "yes"
              }
            }
          ]
        },
        {
          "kind": "Section",
          "title": {
            "en": "USIO Virtual Card Admin"
          },
          "components": [
            {
              "kind": "Explanation",
              "content": {
                "en": "## Virtual Card Flow\n- Payment must propose to generate a card_id_setup_link and associate a card_id to the applicant.\n- Notification must go out after card_id_setup_link exists so that the applicant receives a valid setup link. \n\n- The two fields below serve as placeholders for card id setup link and card id once they get generated."
              }
            },
            {
              "kind": "TextEntry",
              "targetField": "card_id_setup_link",
              "content": {
                "en": "DO NOT DELETE",
              },
              "hidden": true
            },
            {
              "kind": "TextEntry",
              "targetField": "card_id",
              "content": {
                "en": "DO NOT DELETE",
              },
              "hidden": true
            }
          ]
        }
      ]
    } as Collection] : [],
    ...(config.debit.kind === 'GiveCard') ? [{
      "kind": "Collection",
      "name": {
        "en": "GiveCard"
      },
      "options": {},
      "components": [
        {
          "kind": "Section",
          "title": {
            "en": "GiveCard"
          },
          "components": [
            {
              "kind": "Explanation",
              "content": {
                "en": "# Payment Selection\n\nWe currently provide two options for payment. \n\n- **Payment Option 1: Debit Card Pickup**  (debit card has a pin #, applicant can pick up card after approval; usually within 7 days)\n- **Payment Option 2: Debit Card Mailed** (debit card has a pin #, this will take a few weeks to arrive after application approval)",
              }
            },
            {
              "kind": "Select",
              "content": {
                "en": "**What payment method do you prefer?**",
              },
              "targetField": "applicant_payment_type",
              "choices": [
                {
                  "value": "givecard_load",
                  "label": {
                    "en": "Debit Card Pick Up",
                  }
                },
                {
                  "value": "givecard_mail",
                  "label": {
                    "en": "Debit Card Mailed (*could take up to 2 weeks*)",
                  }
                }
              ]
            },
            {
              "kind": "Conditional Block",
              "components": [
                {
                  "kind": "Explanation",
                  "content": {
                    "en": "### AidKit Grantee Cardholder Agreement\n\nThis Grantee Cardholder Agreement (\"Agreement\") is entered into between the card recipient (\"Grantee Cardholder\") and AidKit (\"Technology Platform\") for the use of GiveCard Prepaid Mastercards (\"Cards\"), issued by the Card Platform (“GiveCard”), as part of a charitable program administered by Elevate Boulder.\n\n- **Card Use**: The Grantee Cardholder agrees to use the Card only for authorized transactions and in accordance with the terms and conditions of this Agreement. The Card is not transferable and shall not be used for any illegal or unauthorized transactions.\n\n- **Card Activation and PIN Setting**: The Grantee Cardholder understands that the Card will already be activated upon receipt. A pin will be set as instructed by the Technology Platform. The Card may be subject to certain loading requirements, such as providing personal information, and the Grantee Cardholder agrees to comply with such requirements. Grantee Cardholder is advised that when selecting a PIN, numbers or words that appear in Grantee Cardholder’s date of birth, address, or social security number should not be used. PINs must be memorized and should not be shared with anyone. The PIN should not be written on the Card or kept in the same  location as the Card. Cards should be treated with the same care as cash. Grantee Cardholder shall always protect the Card  and keep it in a safe place and shall not send a Card number in an email or text message. Make sure Card and Access Information is secured with encryption when used to perform transactions over the internet or wireless networks. \n\n- **Card Limitations**: The Card has certain limitations, including but not limited to transaction limits ATM Restrictions, and usage restrictions outlined below. The Grantee Cardholder agrees to abide by such limitations and understands that the Card may be declined if attempted to be used in excess of these limitations.\n\n     - Mastercard imposes certain restrictions on prepaid cards. Purchases attempted to merchants with the following codes and conducting the following businesses may not be accepted: \n        - 6211 Security Brokers/Dealers\n        - 7273 Dating/Escort Services\n        - 7297 Massage Parlors\n        - 7995 Betting/ Casino Gambling\n        - 7800 Government Owned Lotteries\n        - 7801 Government Licensed Online Casinos (Online Gambling)\n        - 7802 Government Licensed Horse/Dog Racing  \n\n     - Transaction Limits:\n        - Maximum per transaction: $5,000\n        - Maximum per day: $5,000\n        - Maximum per month: $5,000\n\n     - ATM Restrictions:\n        - Maximum per transaction: $1,000\n        - Maximum per day: $1,000\n        - Maximum per month: $2,500\n\n- **Online Card Usage**: Grantee Cardholder may use Card for online purchases. The following billing information associated with the Card should go in the billing information section when Grantee Cardholder makes an online purchase:\n\n&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;AidKit  \n&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;2000 S. Colorado Blvd., BLDG 1 - 2000 - #177  \n&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Denver, CO 80222\n\n- **Card Funds**: The Card is a prepaid card and the Grantee Cardholder acknowledges that the Card is not linked to any bank account or credit facility. The Card may only be used for transactions up to the available balance on the Card. The Grantee Cardholder shall not attempt to spend more than the available balance on the Card.\n\n- **Card Expiry**: The expiration date of a physical Card is identified on the front of the Card. If there is a positive balance of funds associated with the Card upon expiration of the subject Card and these Terms are not in default, GiveCard may issue a new Card.\n\n- **Cardholder Liability**: The Grantee Cardholder is responsible for all transactions made using the Card, including any unauthorized transactions, unless the Grantee Cardholder notifies GiveCard in a timely manner as outlined in the following provisions:\n \n     - If a Grantee Cardholder believes their card has been lost or stolen, GiveCard Support should be contacted immediately by calling 908-529-0955. Grantee Cardholder should also lock Card through the cardholder portal (givecard.io/support). \n\n     - Timely Notice Given: If the Grantee Cardholder notifies GiveCard within two business days after learning of the loss or theft of the Card, the Grantee Cardholder's liability shall not exceed the lesser of $50 or the amount of fraudulent transactions that occur before notice to the Technology Platform.\n\n     - Timely Notice Not Given: If the Grantee Cardholder fails to notify the Technology Platform within two business days after learning of the loss or theft of the Card, the Grantee Cardholder's liability shall not exceed the lesser of $500 or the sum of: i) $50 or the amount of fraudulent transactions that occur within the two business days, whichever is less; and ii) The amount of fraudulent transactions that occur after the close of two business days and before notice to the Technology Platform, provided the Technology Platform establishes that these transactions would not have occurred had the Grantee Cardholder notified the Technology Platform within that two-day period.\n\n     - Periodic Statement, Timely Notice Not Given: If a periodic statement shows fraudulent transactions made with a lost or stolen Card, the Grantee Cardholder must notify the Technology Platform within 60 calendar days after the periodic statement was sent; otherwise, the Grantee Cardholder faces unlimited liability for all fraudulent transactions made after the 60-day period. The Grantee Cardholder's liability for fraudulent transactions before the statement is sent, and up to 60 days following, is determined based on the first two tiers of liability (detailed in sections 7(b) and 7(c)).\n\n- **Cardholder Support**: The Grantee Cardholder may contact GiveCard for support related to the Card, including but not limited to Card loading, balance inquiries, transaction disputes, and lost or stolen cards. The contact information for the GiveCard shall be provided to the Grantee Cardholder upon receipt, and is also available here: support@givecard.io, 908-529-0955. Go to [givecard.io/support](givecard.io/support) to access the GiveCard grantee cardholder site. From the Grantee Cardholder site, it is possible to: \n     - Check Card balance\n     - Set or Reset Card PIN number\n     - Find ATMs \n     - View Card spending history (requires creating an account and logging in)",
                  }
                },
                {
                  "kind": "InlineSignature",
                  "targetField": "cardholder_signature",
                  "content": {
                    "en": "**By signing below, the Grantee Cardholder acknowledges that they have read and understood the terms and conditions of this agreement, including the provisions related to timely notice in the event of loss or theft of the access device, and agree to abide by them.**",
                  }
                }
              ],
              "conditionalOn": {
                "kind": "Or",
                "clauses": [
                  {
                    "field": "applicant_payment_type",
                    "kind": "Equals",
                    "value": "givecard_load"
                  },
                  {
                    "field": "applicant_payment_type",
                    "kind": "Equals",
                    "value": "givecard_mail"
                  }
                ]
              }
            }
          ]
        },
        {
          "kind": "Collection",
          "name": {
            "en": "GiveCard Mailing"
          },
          "options": {},
          "components": [
            {
              "kind": "Section",
              "title": {
                "en": "GC Mailing"
              },
              "components": [
                {
                  "kind": "Conditional Block",
                  "components": [
                    {
                      "kind": "Address",
                      "targetField": "usio_mailing_address",
                      "content": {
                        "en": "**Please enter the mailing address where we should send the debit card**",
                      }
                    },
                    {
                      "kind": "Validated",
                      "targetField": "validate_usio_address",
                      "content": {
                        "en": "The address must be in California",
                      },
                      "formula": "if (info.usio_mailing_address_state !== \"CA\") {\n    throw '$content';\n}\nreturn 'hide_ok';"
                    },
                    {
                      "kind": "Validated",
                      "targetField": "validate_usio_address_po_box",
                      "content": {
                        "en": "The address must be a physical address.  It cannot be a P.O. Box",
                      },
                      "formula": "if (!info.usio_mailing_address_street) return 'hide_ok';\n\nconst poBoxVariations = [\n    'po box', 'p.o. box', 'p o box', 'p.o.box', 'po.box', 'pobox', \n    'p o b', 'p.o.b', 'pob'\n];\n\nconst containsPOBox = (address) => {\n    const addressLower = address.toLowerCase();\n    return poBoxVariations.some(variation => addressLower.includes(variation));\n};\n\nif (containsPOBox(info.usio_mailing_address_street)) {\n    throw '$content';  \n}\n\nreturn 'hide_ok';"
                    }
                  ],
                  "conditionalOn": {
                    "field": "applicant_payment_type",
                    "kind": "Equals",
                    "value": "givecard_mail"
                  }
                }
              ]
            },
            {
              "kind": "GiveCard Mailing",
              "targetField": "gc_mailing",
              "mailingAddress": "usio_mailing_address",
              "enableKey": "",
              "condition": {
                "kind": "Click",
                "expr": {
                  "kind": "And",
                  "clauses": []
                }
              }
            }
          ]
        },
        {
          "kind": "Section",
          "title": {
            "en": "GiveCard Debit Card Pick Up",
          },
          "components": [
            {
              "kind": "Explanation",
              "content": {
                "en": "# Debit Card Association",
              }
            },
            {
              "kind": "Show Field",
              "targetField": "legal_name",
              "headerText": {
                "en": "Legal Name",
              }
            },
            {
              "kind": "Show Field",
              "targetField": "selfie",
              "headerText": {
                "en": "Selfie",
              }
            },
            {
              "kind": "Show Field",
              "targetField": "id_doc",
              "headerText": {
                "en": "Photo ID",
              }
            },
            {
              "kind": "Confirmation",
              "targetField": "dc_confirm_identity",
              "content": {
                "en": "The debit card association process is available only after you confirm that the applicant's identity and documentation matches what is in the system.\n\n**Check here only if the applicant's identity and supporting documentation matches what is in the system.**",
              }
            },
            {
              "kind": "Conditional Block",
              "components": [
                {
                  "kind": "Explanation",
                  "content": {
                    "en": "In order to receive payments to your card, please input the 10 character ID on the back of your card beneath \"AidKit\", and then the last 4 digits of the card number.",
                  }
                },
                {
                  "kind": "Card Management",
                  "targetField": "",
                  "cardType": "GiveCard"
                },
                {
                  "kind": "Confirmation",
                  "targetField": "dc_confirm_pickup",
                  "content": {
                    "en": "Check here once Debit Card has been associated and given to the applicant",
                  }
                },
                {
                  "kind": "Explanation",
                  "content": {
                    "en": `All set! \n\nHave questions about the payment? Visit the Payments FAQ page: https://${config.program}.aidkit.org/p/payments-faq`,
                  }
                }
              ],
              "conditionalOn": {
                "field": "dc_confirm_identity",
                "kind": "Equals",
                "value": "yes"
              }
            }
          ]
        }
      ]
    } as Collection] : [],
  ]
});